import React from 'react';

interface FinancialInputsProps {
  purchasePrice: number | '';
  rentPerUnit: number | '';
  capex: number | '';
  vacancyRate: number | '';
  interestRate: number | '';
  financingType: string;
  errors: Record<string, string>;
  onChange: (field: string, value: string | number) => void;
}

const FINANCING_OPTIONS = [
  { value: 'conventional', label: 'Conventional' },
  { value: 'seller', label: 'Seller Finance' },
  { value: 'cash', label: 'Cash' },
  { value: 'hard_money', label: 'Hard Money' },
];

export const FinancialInputsForm: React.FC<FinancialInputsProps> = ({
  purchasePrice,
  rentPerUnit,
  capex,
  vacancyRate,
  interestRate,
  financingType,
  errors,
  onChange,
}) => {
  return (
    <div className="space-y-4">
      <h3 className="text-sm font-semibold text-stone-400 uppercase tracking-wider">
        Financial Inputs
      </h3>

      {/* Purchase Price */}
      <div>
        <label className="block text-sm font-medium text-stone-300 mb-1">Purchase Price ($)</label>
        <input
          type="number"
          value={purchasePrice}
          onChange={(e) => onChange('purchasePrice', e.target.value ? Number(e.target.value) : '')}
          placeholder="350000"
          min={1}
          className={`w-full bg-slate-800 border rounded-lg px-3 py-2.5 text-sm text-ivory placeholder-stone-500 focus:outline-none ${
            errors.purchasePrice ? 'border-red-500 focus:border-red-400' : 'border-slate-600 focus:border-gold'
          }`}
        />
        {errors.purchasePrice && <p className="text-xs text-red-400 mt-1">{errors.purchasePrice}</p>}
      </div>

      {/* Rent per Unit */}
      <div>
        <label className="block text-sm font-medium text-stone-300 mb-1">
          Rent per Unit ($/mo)
        </label>
        <input
          type="number"
          value={rentPerUnit}
          onChange={(e) => onChange('rentPerUnit', e.target.value ? Number(e.target.value) : '')}
          placeholder="1200"
          min={0}
          className={`w-full bg-slate-800 border rounded-lg px-3 py-2.5 text-sm text-ivory placeholder-stone-500 focus:outline-none ${
            errors.rentPerUnit ? 'border-red-500 focus:border-red-400' : 'border-slate-600 focus:border-gold'
          }`}
        />
        {errors.rentPerUnit && <p className="text-xs text-red-400 mt-1">{errors.rentPerUnit}</p>}
      </div>

      {/* CapEx */}
      <div>
        <label className="block text-sm font-medium text-stone-300 mb-1">
          CapEx Budget ($) <span className="text-stone-500">(optional)</span>
        </label>
        <input
          type="number"
          value={capex}
          onChange={(e) => onChange('capex', e.target.value ? Number(e.target.value) : '')}
          placeholder="50000"
          min={0}
          className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2.5 text-sm text-ivory placeholder-stone-500 focus:border-gold focus:outline-none"
        />
      </div>

      {/* Vacancy Rate */}
      <div>
        <label className="block text-sm font-medium text-stone-300 mb-1">
          Vacancy Rate (%)
        </label>
        <input
          type="number"
          value={vacancyRate}
          onChange={(e) => onChange('vacancyRate', e.target.value ? Number(e.target.value) : '')}
          placeholder="5"
          min={0}
          max={25}
          step={0.5}
          className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2.5 text-sm text-ivory placeholder-stone-500 focus:border-gold focus:outline-none"
        />
      </div>

      {/* Interest Rate */}
      <div>
        <label className="block text-sm font-medium text-stone-300 mb-1">
          Interest Rate (%) <span className="text-stone-500">(optional)</span>
        </label>
        <input
          type="number"
          value={interestRate}
          onChange={(e) => onChange('interestRate', e.target.value ? Number(e.target.value) : '')}
          placeholder="7.0"
          min={3}
          max={15}
          step={0.1}
          className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2.5 text-sm text-ivory placeholder-stone-500 focus:border-gold focus:outline-none"
        />
      </div>

      {/* Financing Type */}
      <div>
        <label className="block text-sm font-medium text-stone-300 mb-1">Deal Type</label>
        <div className="grid grid-cols-2 gap-2">
          {FINANCING_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => onChange('financingType', opt.value)}
              className={`px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                financingType === opt.value
                  ? 'bg-gold/20 border-gold text-gold'
                  : 'bg-slate-800 border-slate-600 text-stone-400 hover:border-slate-500 hover:text-stone-300'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};
